/**
 * Pure helpers for the operating-area overlay. They mirror how the Backoffice
 * fleet map draws operating areas (`OperatingAreasPolygon` in fleet-maps-common):
 * all areas of a region are merged into one shape, and everything outside it is
 * greyed out.
 */
import { booleanIntersects } from '@turf/boolean-intersects'
import { difference } from '@turf/difference'
import { featureCollection, polygon } from '@turf/helpers'
import { union } from '@turf/union'
import type {
  Feature,
  FeatureCollection,
  Geometry,
  MultiPolygon,
  Polygon,
  Position,
} from 'geojson'

type AreaGeometry = MultiPolygon | Polygon
export type AreaFeature = Feature<AreaGeometry>

// Web Mercator can't show the poles, so the mask stops at its latitude limit.
const MERCATOR_MAX_LATITUDE = 85
const WORLD = polygon([
  [
    [-180, -MERCATOR_MAX_LATITUDE],
    [180, -MERCATOR_MAX_LATITUDE],
    [180, MERCATOR_MAX_LATITUDE],
    [-180, MERCATOR_MAX_LATITUDE],
    [-180, -MERCATOR_MAX_LATITUDE],
  ],
])

const isAreaGeometry = (
  geometry: Geometry | null | undefined,
): geometry is AreaGeometry =>
  geometry?.type === 'MultiPolygon' || geometry?.type === 'Polygon'

const toAreaFeature = (geometry: AreaGeometry): AreaFeature => ({
  type: 'Feature',
  properties: {},
  geometry,
})

const parseAreaGeometry = (geometry: string): AreaFeature | null => {
  try {
    const parsed: Geometry = JSON.parse(geometry)
    return isAreaGeometry(parsed) ? toAreaFeature(parsed) : null
  } catch {
    return null
  }
}

/** Merges areas into one shape. Returns null when there are none. */
export const mergeAreaFeatures = (
  features: ReadonlyArray<AreaFeature>,
): AreaFeature | null => {
  if (features.length === 0) return null
  if (features.length === 1) return features[0]
  return union(featureCollection([...features]))
}

/**
 * Merges the GeoJSON geometry strings of a region's operating areas into one
 * shape. Areas whose geometry can't be parsed are skipped. Returns null when no
 * area has a usable geometry.
 */
export const mergeOperatingAreas = (
  areas: ReadonlyArray<{ geometry: string }>,
): AreaFeature | null =>
  mergeAreaFeatures(
    areas
      .map(area => parseAreaGeometry(area.geometry))
      .filter(feature => feature !== null),
  )

/** The polygons of a hardcoded outline (the static geonet GeoJSON). */
export const outlineAreaFeatures = (
  outline: FeatureCollection,
): AreaFeature[] =>
  outline.features
    .map(feature => feature.geometry)
    .filter(isAreaGeometry)
    .map(toAreaFeature)

// The polygon operations below run on the main thread whenever a geonet is
// toggled, and are slow for real geonets. Loaded GeoJSON is never mutated, so
// results are cached by the identity of their inputs.
const mergedOutlines = new WeakMap<FeatureCollection, AreaFeature | null>()
const outsideMasks = new WeakMap<AreaFeature, AreaFeature | null>()

/**
 * Merges the polygons of a hardcoded outline (the static geonet GeoJSON) into
 * one shape, the fallback when the live operating areas can't be loaded.
 */
export const mergeOutlineFeatures = (
  outline: FeatureCollection,
): AreaFeature | null => {
  if (mergedOutlines.has(outline)) return mergedOutlines.get(outline) ?? null
  const merged = mergeAreaFeatures(outlineAreaFeatures(outline))
  mergedOutlines.set(outline, merged)
  return merged
}

const objectIds = new WeakMap<object, number>()
let nextObjectId = 0
const objectId = (value: object) => {
  let id = objectIds.get(value)
  if (id === undefined) {
    id = nextObjectId++
    objectIds.set(value, id)
  }
  return id
}

// Enough to keep every combination a user flips between while toggling.
const RECENT_UNSHADED_AREAS_LIMIT = 8
const recentUnshadedAreas = new Map<string, AreaFeature | null>()

/**
 * The part of the map the shade leaves clear: the operating area and the shown
 * geonets, merged into one shape. Both the shade and the lane clip need it on
 * every geonet toggle, so recent results are kept and toggling back is free.
 */
export const buildUnshadedArea = (
  operatingArea: AreaFeature | null,
  geonetOutlines: ReadonlyArray<FeatureCollection>,
): AreaFeature | null => {
  const key = [
    operatingArea ? objectId(operatingArea) : '-',
    ...geonetOutlines.map(objectId),
  ].join(',')
  if (recentUnshadedAreas.has(key)) {
    const hit = recentUnshadedAreas.get(key) ?? null
    // Move it to the end so the least recently used result is dropped first.
    recentUnshadedAreas.delete(key)
    recentUnshadedAreas.set(key, hit)
    return hit
  }
  const areas = geonetOutlines
    .map(mergeOutlineFeatures)
    .filter(area => area !== null)
  if (operatingArea) areas.unshift(operatingArea)
  const merged = mergeAreaFeatures(areas)
  recentUnshadedAreas.set(key, merged)
  if (recentUnshadedAreas.size > RECENT_UNSHADED_AREAS_LIMIT) {
    const [oldest] = recentUnshadedAreas.keys()
    recentUnshadedAreas.delete(oldest)
  }
  return merged
}

/**
 * The whole world with the unshaded area (`buildUnshadedArea`) cut out of it,
 * so the shade only covers what is outside the operating area and shown geonets.
 */
export const buildOutsideMask = (area: AreaFeature): AreaFeature | null => {
  if (outsideMasks.has(area)) return outsideMasks.get(area) ?? null
  const mask = difference(featureCollection([WORLD, area]))
  outsideMasks.set(area, mask)
  return mask
}

type BBox = [minX: number, minY: number, maxX: number, maxY: number]

const positionsOf = (geometry: Geometry): Position[] => {
  switch (geometry.type) {
    case 'Point':
      return [geometry.coordinates]
    case 'MultiPoint':
    case 'LineString':
      return geometry.coordinates
    case 'MultiLineString':
    case 'Polygon':
      return geometry.coordinates.flat()
    case 'MultiPolygon':
      return geometry.coordinates.flat(2)
    case 'GeometryCollection':
      return geometry.geometries.flatMap(positionsOf)
  }
}

const bboxOf = (geometry: Geometry): BBox => {
  const box: BBox = [Infinity, Infinity, -Infinity, -Infinity]
  for (const [x, y] of positionsOf(geometry)) {
    box[0] = Math.min(box[0], x)
    box[1] = Math.min(box[1], y)
    box[2] = Math.max(box[2], x)
    box[3] = Math.max(box[3], y)
  }
  return box
}

const bboxesOverlap = (a: BBox, b: BBox) =>
  a[0] <= b[2] && b[0] <= a[2] && a[1] <= b[3] && b[1] <= a[3]

/**
 * Whether any of the features (a lane set) touches the area. Features whose
 * bounding box misses the area are skipped before the exact test.
 */
export const touchesArea = (
  features: FeatureCollection,
  area: AreaFeature,
): boolean => {
  const areaBox = bboxOf(area.geometry)
  return features.features.some(
    feature =>
      feature.geometry !== null &&
      bboxesOverlap(bboxOf(feature.geometry), areaBox) &&
      booleanIntersects(feature, area),
  )
}
